import { useTranslations } from 'next-intl'
import { LocaleLink } from '@/components/LocaleLink'
import type { ReactNode } from 'react'

/** Atributos padrão para links de artigo externos (sem prefetch, seguro). */
export const externalArticleLink = {
  prefetch: false as const,
  rel: 'noopener noreferrer' as const,
}

/** Tempo de leitura (rótulo pequeno, uppercase). Compartilhado entre faixas. */
export function ReadTime({ children }: { children: ReactNode }) {
  const t = useTranslations('Blog')
  return (
    <p className="text-muted-foreground mt-1 text-[11px] font-semibold tracking-wide uppercase">
      <span className="sr-only">{t('readingTimeLabel')} </span>
      {children}
    </p>
  )
}

/**
 * Rótulo de categoria (10px, bold, uppercase). NÃO usar Badge nas listas.
 * Com `href` → vira link para o arquivo da categoria (`/{categoryUrlBase}/{slug}`); senão, texto.
 * IMPORTANTE: só passar `href` quando o rótulo NÃO estiver aninhado dentro de outro `<a>`.
 */
export function CategoryLabel({
  children,
  href,
  className,
}: {
  children: ReactNode
  href?: string
  className?: string
}) {
  const baseCls = 'text-muted-foreground text-[10px] font-bold tracking-wide uppercase'
  if (href) {
    return (
      <LocaleLink
        href={href}
        className={[baseCls, 'block hover:text-foreground hover:underline', className]
          .filter(Boolean)
          .join(' ')}
      >
        {children}
      </LocaleLink>
    )
  }
  return <p className={[baseCls, className].filter(Boolean).join(' ')}>{children}</p>
}

export type SectionHeadingLevel = 'h1' | 'h2' | 'h3'

export function SectionHeading({
  level = 'h2',
  id,
  className,
  children,
}: {
  level?: SectionHeadingLevel
  id?: string
  className?: string
  children: ReactNode
}) {
  const Tag = level === 'h1' ? 'h1' : level === 'h3' ? 'h3' : 'h2'
  return (
    <Tag id={id} className={className}>
      {children}
    </Tag>
  )
}
